import React, { useState, useEffect } from 'react';
import { Download, RefreshCw, Users, Calendar, Filter, ArrowLeft, Search } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [filteredEnquiries, setFilteredEnquiries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [procedureFilter, setProcedureFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');

  useEffect(() => {
    fetchEnquiries();
  }, []);

  useEffect(() => {
    filterEnquiries();
  }, [enquiries, searchTerm, procedureFilter, dateFilter]);

  const fetchEnquiries = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API}/enquiries?limit=100`);
      if (response.ok) {
        const data = await response.json();
        setEnquiries(data);
      } else {
        toast.error('Failed to fetch enquiries');
      }
    } catch (error) {
      console.error('Error fetching enquiries:', error);
      toast.error('Error connecting to server');
    } finally {
      setIsLoading(false);
    }
  };

  const filterEnquiries = () => {
    let filtered = [...enquiries];

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(e => 
        e.name?.toLowerCase().includes(term) ||
        e.phone?.toLowerCase().includes(term) ||
        e.country?.toLowerCase().includes(term) ||
        e.city?.toLowerCase().includes(term)
      );
    }

    // Procedure filter
    if (procedureFilter !== 'all') {
      filtered = filtered.filter(e => e.procedure === procedureFilter);
    }

    // Date filter
    if (dateFilter !== 'all') {
      const now = new Date();
      filtered = filtered.filter(e => {
        const enquiryDate = new Date(e.timestamp);
        if (dateFilter === 'today') {
          return enquiryDate.toDateString() === now.toDateString();
        } else if (dateFilter === 'week') {
          const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          return enquiryDate >= weekAgo;
        } else if (dateFilter === 'month') {
          const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          return enquiryDate >= monthAgo;
        }
        return true;
      });
    }

    setFilteredEnquiries(filtered);
  };

  const exportToCSV = () => {
    if (filteredEnquiries.length === 0) {
      toast.error('No enquiries to export');
      return;
    }

    const headers = ['Timestamp', 'Name', 'Phone', 'City', 'Country', 'Procedure', 'Reports Available', 'Timeframe', 'Synced to Sheets'];
    
    const csvContent = [
      headers.join(','),
      ...filteredEnquiries.map(e => [
        new Date(e.timestamp).toLocaleString(),
        `"${e.name || ''}"`,
        `"${e.phone || ''}"`,
        `"${e.city || ''}"`,
        `"${e.country || ''}"`,
        `"${e.procedure || ''}"`,
        `"${e.reportsAvailable || ''}"`,
        `"${e.timeframe || ''}"`,
        e.synced_to_sheets ? 'Yes' : 'No'
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `curispath_enquiries_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success(`Exported ${filteredEnquiries.length} enquiries to CSV`);
  };

  const uniqueProcedures = [...new Set(enquiries.map(e => e.procedure).filter(Boolean))];

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-IN', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-page)' }}>
      {/* Header */}
      <header style={{ background: 'var(--text-primary)', padding: '1rem 0' }}>
        <div className="container">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <a 
                href="/" 
                className="text-white/70 hover:text-white transition-colors flex items-center gap-2"
              >
                <ArrowLeft size={18} />
                <span className="hidden sm:inline">Back to Site</span>
              </a>
              <span className="text-white/30">|</span>
              <span className="text-white font-bold text-xl">Curis Path</span>
            </div>
            <span className="text-white/70 text-sm">Admin Dashboard</span>
          </div>
        </div>
      </header>

      <div className="container py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="voice-card accent-blue p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'var(--accent-blue-400)' }}>
                <Users className="text-white" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{enquiries.length}</p>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Total Enquiries</p>
              </div>
            </div>
          </div>
          
          <div className="voice-card accent-green p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: '#22c55e' }}>
                <Calendar className="text-white" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
                  {enquiries.filter(e => {
                    const today = new Date().toDateString();
                    return new Date(e.timestamp).toDateString() === today;
                  }).length}
                </p>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Today's Enquiries</p>
              </div>
            </div>
          </div>
          
          <div className="voice-card accent-orange p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'var(--accent-orange-400)' }}>
                <Filter className="text-white" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{filteredEnquiries.length}</p>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Filtered Results</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters & Actions */}
        <div className="voice-card accent-grey p-5 mb-6">
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
            <div className="flex flex-col sm:flex-row gap-3 flex-1 w-full lg:w-auto">
              {/* Search */}
              <div className="relative flex-1 max-w-xs">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-secondary)' }} />
                <input
                  type="text"
                  placeholder="Search name, phone, country..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 rounded-lg text-sm"
                  style={{
                    border: '1px solid var(--border-input)',
                    background: 'var(--bg-card)',
                    color: 'var(--text-primary)'
                  }}
                />
              </div>

              {/* Procedure Filter */}
              <select
                value={procedureFilter}
                onChange={(e) => setProcedureFilter(e.target.value)}
                className="px-3 py-2 rounded-lg text-sm"
                style={{
                  border: '1px solid var(--border-input)',
                  background: 'var(--bg-card)',
                  color: 'var(--text-primary)'
                }}
              >
                <option value="all">All Procedures</option>
                {uniqueProcedures.map(proc => (
                  <option key={proc} value={proc}>{proc}</option>
                ))}
              </select>

              {/* Date Filter */}
              <select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="px-3 py-2 rounded-lg text-sm"
                style={{
                  border: '1px solid var(--border-input)',
                  background: 'var(--bg-card)',
                  color: 'var(--text-primary)'
                }}
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="week">Last 7 Days</option>
                <option value="month">Last 30 Days</option>
              </select>
            </div>

            <div className="flex gap-3">
              <button
                onClick={fetchEnquiries}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all hover:scale-105"
                style={{ 
                  border: '1px solid var(--border-input)',
                  background: 'var(--bg-card)',
                  color: 'var(--text-primary)'
                }}
                data-testid="refresh-btn"
              >
                <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
                Refresh
              </button>
              
              <button
                onClick={exportToCSV}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white transition-all hover:scale-105"
                style={{ 
                  background: '#22c55e',
                  boxShadow: '0 2px 8px rgba(34, 197, 94, 0.3)'
                }}
                data-testid="export-csv-btn"
              >
                <Download size={16} />
                Export CSV
              </button>
            </div>
          </div>
        </div>

        {/* Enquiries Table */}
        <div className="voice-card p-0 overflow-hidden">
          {isLoading ? (
            <div className="p-12 text-center">
              <RefreshCw size={32} className="animate-spin mx-auto mb-4" style={{ color: 'var(--text-secondary)' }} />
              <p style={{ color: 'var(--text-secondary)' }}>Loading enquiries...</p>
            </div>
          ) : filteredEnquiries.length === 0 ? (
            <div className="p-12 text-center">
              <Users size={48} className="mx-auto mb-4" style={{ color: 'var(--text-secondary)', opacity: 0.5 }} />
              <p className="text-lg font-medium mb-2" style={{ color: 'var(--text-primary)' }}>No enquiries found</p>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                {enquiries.length > 0 ? 'Try adjusting your filters' : 'Enquiries will appear here when patients submit the form'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr style={{ background: 'var(--bg-section)' }}>
                    <th className="text-left px-4 py-3 text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Date</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Name</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Phone</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Location</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Procedure</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Reports</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Timeframe</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEnquiries.map((enquiry, index) => (
                    <tr 
                      key={enquiry.id || index}
                      className="border-t hover:bg-gray-50 transition-colors"
                      style={{ borderColor: 'var(--border-light)' }}
                    >
                      <td className="px-4 py-3 text-sm" style={{ color: 'var(--text-secondary)' }}>
                        {formatDate(enquiry.timestamp)}
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-medium" style={{ color: 'var(--text-primary)' }}>{enquiry.name}</span>
                      </td>
                      <td className="px-4 py-3">
                        <a 
                          href={`tel:${enquiry.phone}`}
                          className="text-sm hover:underline"
                          style={{ color: 'var(--accent-blue-400)' }}
                        >
                          {enquiry.phone}
                        </a>
                      </td>
                      <td className="px-4 py-3 text-sm" style={{ color: 'var(--text-secondary)' }}>
                        {[enquiry.city, enquiry.country].filter(Boolean).join(', ') || '-'}
                      </td>
                      <td className="px-4 py-3">
                        <span 
                          className="px-2 py-1 rounded-full text-xs font-medium"
                          style={{ 
                            background: 'var(--accent-purple-200)',
                            color: 'var(--accent-purple-400)'
                          }}
                        >
                          {enquiry.procedure || '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm" style={{ color: 'var(--text-secondary)' }}>
                        {enquiry.reportsAvailable === 'yes' ? '✓ Yes' : enquiry.reportsAvailable === 'no' ? '✗ No' : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm" style={{ color: 'var(--text-secondary)' }}>
                        {enquiry.timeframe || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer Note */}
        <p className="text-center text-sm mt-6" style={{ color: 'var(--text-secondary)' }}>
          Data is stored securely in MongoDB. Export to CSV for offline access or sharing.
        </p>
      </div>
    </div>
  );
};

export default AdminDashboard;
